import React, { forwardRef, useRef, useEffect } from 'react';
import { classNames } from '../../utils/classNames/classNames.esm.js';
import { mergeRefs } from '../../utils/refs/refs.esm.js';
import '@popperjs/core';
import { testIdGen } from '../../utils/testIdGen.esm.js';
import '../Icon/ImgIcon.esm.js';
import { MenuIcon } from '../Icon/SemanticIcons.esm.js';
import isIconOnly from './isIconOnly.esm.js';
import validateProps from './validateProps.esm.js';

const defaultProps = {
    appearance: "secondary",
    isMenu: false,
    size: "md",
    disabled: false,
    buttonType: "default",
};
function makeClassName(props) {
    const { append, prepend, addClassName, buttonPosition, disabled, isLoading, icon, isBtnGroup, isFixed, fullWidth, isIconGroup, isMenu, isOpen, isSelected = false, isSplit, size = "md", appearance = "secondary", buttonType = "default", } = props;
    const iconOnly = isIconOnly(props);
    const defaultClassNames = classNames("tw-relative tw-p-0 tw-border-solid tw-font-lato tw-cursor-pointer tw-whitespace-nowrap tw-box-border tw-rounded tw-leading-none tw-outline-none tw-flex tw-items-center tw-relative tw-max-w-full  focus-visible:tw-outline-2 focus-visible:tw-outline-blue-500 dark:focus-visible:tw-outline-blue-300 focus-visible:tw-outline-offset-1", buttonType === "default" ? "tw-font-semibold" : "");
    const sizeClassName = {
        lg: isBtnGroup
            ? "tw-text-sm tw-h-10 tw-w-38 tw-justify-between"
            : isFixed
                ? "tw-text-sm tw-h-10 tw-w-22 tw-justify-center"
                : iconOnly && isMenu
                    ? "tw-text-sm tw-h-10"
                    : iconOnly
                        ? "tw-text-sm tw-h-10 tw-w-10"
                        : "tw-text-sm tw-h-10",
        md: isBtnGroup
            ? "tw-text-sm tw-h-8 tw-w-34 tw-justify-between"
            : isFixed
                ? "tw-text-xs tw-h-8 tw-w-20 tw-justify-center"
                : iconOnly && isMenu
                    ? "tw-text-xs tw-h-8"
                    : iconOnly
                        ? "tw-text-xs tw-h-8 tw-w-8"
                        : "tw-text-xs tw-h-8",
        sm: isBtnGroup
            ? "tw-text-sm tw-h-6 tw-w-30 tw-justify-between"
            : isFixed
                ? "tw-text-xs tw-h-6 tw-w-18 tw-justify-center"
                : iconOnly && isMenu
                    ? "tw-text-xs tw-h-6"
                    : iconOnly
                        ? "tw-text-xs tw-h-6 tw-w-6"
                        : "tw-text-xs tw-h-6",
    }[size];
    const spacingClassName = {
        lg: isSplit
            ? "tw-py-3 tw-pl-2.5 tw-pr-2.5"
            : iconOnly && isMenu
                ? "tw-pl-3 tw-pr-2"
                : iconOnly
                    ? "tw-p-2.5"
                    : isMenu && icon
                        ? "tw-py-2 tw-pl-3 tw-pr-2"
                        : isMenu
                            ? "tw-py-2 tw-pl-4 tw-pr-2"
                            : icon
                                ? "tw-py-2 tw-pl-3 tw-pr-4"
                                : "tw-py-2 tw-px-4",
        md: isSplit
            ? "tw-py-2 tw-pl-1.5 tw-pr-1.5"
            : iconOnly && isMenu
                ? "tw-pl-2 tw-pr-1.5"
                : iconOnly
                    ? "tw-p-1.5"
                    : isMenu && icon
                        ? "tw-py-2 tw-pl-3 tw-pr-2"
                        : isMenu
                            ? "tw-py-2 tw-pl-4 tw-pr-2"
                            : icon
                                ? "tw-py-2 tw-pl-3 tw-pr-4"
                                : "tw-py-2 tw-px-4",
        sm: isSplit
            ? "tw-py-2 tw-pl-0.5 tw-pr-0.5"
            : iconOnly && isMenu
                ? "tw-pl-1 tw-pr-0.5"
                : iconOnly
                    ? "tw-p-0.5"
                    : isMenu && icon
                        ? "tw-py-2 tw-pl-1 tw-pr-0.5"
                        : isMenu
                            ? "tw-py-2 tw-pl-2 tw-pr-0.5"
                            : icon
                                ? "tw-py-2 tw-pl-1 tw-pr-2"
                                : "tw-py-2 tw-px-2",
    }[size];
    /**
     * Before trying to touch up, or modify Button appearances, please refer to the following documentation: https://confluence.paloaltonetworks.local/x/qSnbD
     */
    const secondaryDisabledStyle = classNames(buttonType === "default"
        ? disabled
            ? "tw-opacity-40"
            : "hover:tw-text-blue-700 dark:hover:tw-text-blue-300 hover:tw-bg-blue-100 dark:hover:tw-bg-blue-700 hover:tw-border-blue-700 dark:hover:tw-border-blue-500"
        : disabled
            ? "tw-opacity-40"
            : "hover:tw-text-gray-500 hover:tw-border-blue-700");
    const typeClassName = {
        primary: classNames("tw-border-0 tw-group tw-text-dark-bg dark:tw-text-blue-100 tw-bg-blue-600 dark:tw-bg-blue-700 focus-visible:tw-bg-blue-700 dark:focus-visible:tw-bg-blue-600", disabled
            ? "tw-opacity-40"
            : "hover:tw-bg-blue-700 dark:hover:tw-bg-blue-600 active:tw-bg-blue-800 dark:active:tw-bg-blue-500", isLoading && "tw-bg-blue-800 dark:tw-bg-blue-500 tw-justify-center", isMenu &&
            isOpen &&
            "tw-bg-blue-800 dark:tw-bg-blue-500 hover:tw-bg-blue-800 dark:hover:tw-bg-blue-500"),
        secondary: classNames("tw-border tw-group tw-bg-white dark:tw-bg-gray-1050 dark:tw-text-blue-400 dark:tw-border-blue-700 dark:active:tw-text-blue-200 dark:active:tw-bg-blue-600 dark:active:tw-border-blue-400 focus-visible:tw-bg-blue-100 dark:focus-visible:tw-bg-blue-700 focus-visible:tw-border-blue-700 dark:focus-visible:tw-border-blue-500 focus-visible:tw-text-blue-700 dark:focus-visible:tw-text-blue-300", buttonType === "default"
            ? "tw-text-link active:tw-text-blue-800 active:tw-bg-blue-200 active:tw-border-blue-800 tw-border-gray-300"
            : `tw-text-gray-500 active:tw-border-blue-700 ${isOpen ? "tw-border-blue-700" : "tw-border-gray-300"}`, secondaryDisabledStyle, isLoading &&
            "tw-justify-center tw-bg-blue-200 dark:tw-bg-blue-600 tw-text-blue-800 dark:tw-text-blue-200 tw-border-blue-800 dark:tw-border-blue-400", isMenu &&
            isOpen &&
            "dark:tw-bg-blue-600 dark:tw-text-blue-200 dark:tw-border-blue-400 dark:hover:tw-bg-blue-600 dark:hover:tw-text-blue-200 dark:hover:tw-border-blue-400", isMenu &&
            isOpen &&
            buttonType === "default" &&
            "tw-bg-blue-200 tw-text-blue-800 tw-border-blue-800 hover:tw-bg-blue-200 hover:tw-text-blue-800 hover:tw-border-blue-800", isSelected && buttonType === "form-select" && "tw-text-gray-900", !disabled && !(isMenu && isOpen) && "dark:hover:tw-bg-blue-700"),
        clear: classNames("tw-border-0 tw-group tw-bg-transparent tw-text-link dark:tw-text-blue-400 focus-visible:tw-bg-blue-600/[.06] dark:focus-visible:tw-bg-blue-700 focus-visible:tw-text-blue-700 dark:focus-visible:tw-text-blue-300", disabled
            ? "tw-opacity-40"
            : "hover:tw-text-blue-700 dark:hover:tw-text-blue-300 hover:tw-bg-blue-600/[.06] active:tw-text-blue-800 dark:active:tw-text-blue-200 active:tw-bg-blue-600/[.16] dark:active:tw-bg-blue-600", isMenu &&
            isOpen &&
            "tw-bg-blue-200 dark:tw-bg-blue-600 tw-text-blue-800 dark:tw-text-blue-200 hover:tw-bg-blue-200 dark:hover:tw-bg-blue-600 hover:tw-text-blue-800 dark:hover:tw-text-blue-200", !disabled && !(isMenu && isOpen) && "dark:hover:tw-bg-blue-700"),
        tertiary: classNames("tw-border tw-group tw-bg-white dark:tw-bg-gray-1050 tw-border-gray-300 dark:tw-border-gray-900 dark:tw-text-gray-400 focus-visible:tw-text-gray-800 dark:focus-visible:tw-text-gray-300 focus-visible:tw-bg-gray-200 dark:focus-visible:tw-bg-gray-800 focus-visible:tw-border-gray-800 dark:focus-visible:tw-border-gray-600", disabled
            ? "tw-opacity-40"
            : "hover:tw-text-gray-800 dark:hover:tw-text-gray-300 hover:tw-bg-gray-200 dark:hover:tw-bg-gray-800 hover:tw-border-gray-800 dark:hover:tw-border-gray-600 active:tw-border-gray-900 dark:active:tw-border-gray-500 active:tw-text-gray-900 dark:active:tw-text-gray-200 active:tw-bg-gray-300 dark:active:tw-bg-gray-700", isMenu && isOpen
            ? "tw-bg-gray-300 dark:tw-bg-gray-700 tw-text-gray-900 dark:tw-text-gray-200 tw-border-gray-900 dark:tw-border-gray-500 hover:tw-bg-gray-300 dark:hover:tw-bg-gray-700 hover:tw-text-gray-900 dark:hover:tw-text-gray-200 hover:tw-border-gray-900 dark:hover:tw-border-gray-500"
            : "tw-text-gray-700"),
        "tertiary-clear": classNames("tw-border-0 tw-group tw-bg-transparent dark:tw-text-gray-400 focus-visible:tw-bg-gray-200 dark:focus-visible:tw-bg-gray-800 focus-visible:tw-text-gray-800 dark:focus-visible:tw-text-gray-300", disabled
            ? "tw-opacity-40"
            : " hover:tw-text-gray-800 dark:hover:tw-text-gray-300 hover:tw-bg-gray-700/[.06] dark:hover:tw-bg-gray-800 active:tw-text-gray-900 dark:active:tw-text-gray-200 active:tw-bg-gray-700/[.16] dark:active:tw-bg-gray-700", isMenu && isOpen
            ? "tw-bg-gray-300 dark:tw-bg-gray-700 tw-text-gray-900 dark:tw-text-gray-200 hover:tw-bg-gray-300 dark:hover:tw-bg-gray-700 hover:tw-text-gray-900 dark:hover:tw-text-gray-200"
            : "tw-text-gray-700"),
        "primary-destructive": classNames("tw-border-0 tw-group tw-text-dark-bg dark:tw-text-red-100 tw-bg-red-500 dark:tw-bg-red-600 focus-visible:tw-bg-red-600 dark:focus-visible:tw-bg-red-500", disabled
            ? "tw-opacity-40"
            : " hover:tw-bg-red-600 dark:hover:tw-bg-red-500 active:tw-bg-red-700 dark:active:tw-bg-red-400", isMenu &&
            isOpen &&
            "tw-bg-red-700 dark:tw-bg-red-400 hover:tw-bg-red-700 dark:hover:tw-bg-red-400"),
        "secondary-destructive": classNames("tw-border tw-group tw-bg-white tw-border-gray-300 dark:tw-border-gray-900 tw-text-red-500 dark:tw-text-red-400 focus-visible:tw-bg-red-100 dark:focus-visible:tw-bg-red-700 focus-visible:tw-text-red-600 dark:focus-visible:tw-text-red-300 focus-visible:tw-border-red-600 dark:focus-visible:tw-border-red-500", disabled
            ? "tw-opacity-40"
            : "hover:tw-text-red-600 dark:hover:tw-text-red-300 hover:tw-bg-red-100  hover:tw-border-red-600 dark:hover:tw-border-red-500 active:tw-text-red-700 dark:active:tw-text-red-200 active:tw-bg-red-200 dark:active:tw-bg-red-600 active:tw-border-red-700 dark:active:tw-border-red-400", isMenu && isOpen
            ? "tw-bg-red-200 dark:tw-bg-red-600 tw-text-red-700 dark:tw-text-red-200 tw-border-red-700 dark:tw-border-red-400 hover:tw-bg-red-200 dark:hover:tw-bg-red-600 hover:tw-text-red-700 dark:hover:tw-text-red-200 hover:tw-border-red-700 dark:hover:tw-border-red-400"
            : "dark:tw-bg-gray-1050", !disabled && !(isMenu && isOpen) && " dark:hover:tw-bg-red-700"),
    };
    return classNames(addClassName, defaultClassNames, sizeClassName, spacingClassName, isLoading || disabled ? "tw-cursor-not-allowed" : null, typeClassName[appearance], fullWidth ? "tw-w-full tw-flex-1" : null, append
        ? `tw-rounded-l-none ${appearance === "primary" && !isIconGroup ? "tw-ml-px" : "tw--ml-px"}  hover:tw-z-10 focus:tw-z-10`
        : "", prepend ? "tw-rounded-r-none hover:tw-z-10 focus:tw-z-10" : "", isMenu && isOpen && "tw-z-10", isIconGroup &&
        buttonPosition === "middle" &&
        appearance === "primary" &&
        "tw-border-0 hover:tw-border-0 active:tw-border-0 focus:tw-ring-offset-1 active:tw-ring-offset-1", isIconGroup &&
        (buttonPosition === "middle" || buttonPosition === "right") &&
        (appearance === "secondary" || appearance === "tertiary") &&
        "tw-border-l-transparent");
}
/**
 * Lets users trigger an action. All extra props will be passed to the button element.
 *
 * ### Import Guide
 *
 * ```jsx
 * import { Button } from "@panwds/react-ui";
 * ```
 */
const Button = forwardRef((props, userRef) => {
    /* eslint-disable @typescript-eslint/no-unused-vars */
    const { addClassName, appearance, buttonPosition, focusOnMount, fullWidth, children, isLoading, dataMetrics, dataTestId, disabled, icon, isBtnGroup, isFixed, isIconGroup, isMenu, isOpen, isSelected, isSplit, size, buttonType, ...rest } = props;
    /* eslint-enable @typescript-eslint/no-unused-vars */
    validateProps(props);
    const className = makeClassName(props);
    const ariaProps = isMenu ? { ["aria-expanded"]: isOpen } : {};
    const buttonRef = useRef(null);
    const ref = mergeRefs([buttonRef, userRef]);
    useEffect(() => {
        var _a;
        if (focusOnMount)
            (_a = buttonRef.current) === null || _a === void 0 ? void 0 : _a.focus();
    }, []);
    return (React.createElement("button", { "data-metrics": dataMetrics, "data-testid": dataTestId ||
            `button-${typeof children === "string" && testIdGen(children)}`, ref: ref, type: "button", ...ariaProps, ...rest, disabled: disabled || isLoading, className: className },
        icon && (React.createElement("span", { className: classNames("tw-w-5 tw-h-5 tw-grid tw-items-center tw-justify-items-center", isLoading ? "tw-opacity-0" : "tw-opacity-100", appearance === "primary" && "tw-text-dark-bg", appearance === "secondary" && disabled
                ? "tw-opacity-40 tw-cursor-not-allowed"
                : isLoading && "tw-text-link", appearance === "clear" && disabled
                ? "tw-opacity-40 tw-cursor-not-allowed"
                : isLoading && "tw-text-link", isIconOnly(props)
                ? "tw-w-4"
                : size === "sm"
                    ? "tw-mr-0.5"
                    : "tw-mr-1") }, icon)),
        isBtnGroup && (React.createElement("span", { className: "tw-truncate tw-flex-1 tw-text-left" }, children)),
        !isBtnGroup && children,
        isMenu && React.createElement(MenuIcon, { isOpen: isOpen, buttonType: buttonType })));
});
Button.defaultProps = defaultProps;
Button.displayName = "Button";

export { Button };
//# sourceMappingURL=Button.esm.js.map
